/**
 * Hook personalizado para caché inteligente de avatares.
 * Implementa caché con localStorage para optimizar carga de imágenes.
 * Adapta la lógica de expo-image para el entorno web.
 * 
 * @param {string} userId - ID del usuario
 * @param {string} imageUrl - URL de la imagen del avatar
 * @param {Array} nearbyUsersIds - Lista de IDs de usuarios cercanos
 * @returns {Object} - Objeto con source, cacheKey, isNearbyUser, etc.
 */

import { useMemo, useCallback, useEffect } from 'react';

// Imagen por defecto cuando no hay avatar (null para usar div de color morado)
const DEFAULT_IMAGE = null;

// Set global para tracking de avatares precargados exitosamente (evita duplicaciones)
const preloadedAvatars = new Set();

// Set global para tracking de avatares que fallaron (evita reintentos infinitos)
const failedAvatars = new Set();

// Map global para tracking de precargas en curso (evita condiciones de carrera)
const loadingAvatars = new Map();

/**
 * Genera una clave de caché única para el avatar
 * @param {string} userId - ID del usuario
 * @returns {string} - Clave de caché
 */
const getCacheKey = (userId) => {
  if (!userId) return null;
  return `avatar_${userId}`;
};

/**
 * Obtiene la fuente de la imagen con fallback
 * @param {string} imageUrl - URL de la imagen
 * @returns {Object|null} - Objeto source con uri o null para usar div de color
 */
const getImageSource = (imageUrl) => {
  if (!imageUrl) return null;
  return { uri: imageUrl };
};

/**
 * Hook principal para caché de avatares
 */
export function useCachedAvatar(userId, imageUrl, nearbyUsersIds = []) {
  /**
   * Determina si el usuario es cercano basado en la lista de IDs
   * Los usuarios cercanos obtienen caché de alta prioridad
   */
  const isNearbyUser = useMemo(() => {
    return nearbyUsersIds.includes(userId);
  }, [userId, nearbyUsersIds]);

  /**
   * Genera la clave de caché para este avatar
   */
  const cacheKey = useMemo(() => {
    return getCacheKey(userId);
  }, [userId]);

  /**
   * Obtiene la fuente de la imagen con fallback
   */
  const source = useMemo(() => {
    return getImageSource(imageUrl);
  }, [imageUrl]);

  /**
   * Precarga el avatar si es un usuario cercano
   * Usa la API de Image del navegador para precarga
   * Evita precargas duplicadas usando sets globales y tracking de carga
   */
  const preloadAvatar = useCallback(async () => {
    if (!source?.uri || !userId) return;

    const preloadKey = `${userId}_${source.uri}`;

    // Verificar si ya fue precargado exitosamente
    if (preloadedAvatars.has(preloadKey)) {
      return;
    }

    // Verificar si ya falló anteriormente para evitar reintentos infinitos
    if (failedAvatars.has(preloadKey)) {
      return;
    }

    // Verificar si ya está cargando actualmente (evita condiciones de carrera)
    if (loadingAvatars.has(preloadKey)) {
      return;
    }

    try {
      // Marcar como en curso
      loadingAvatars.set(preloadKey, true);

      const img = new Image();
      img.src = source.uri;

      // Esperar a que la imagen se cargue
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        // Timeout de 5 segundos
        setTimeout(() => reject(new Error('Timeout')), 5000);
      });

      // Marcar como precargado exitosamente
      preloadedAvatars.add(preloadKey);
    } catch (error) {
      // Marcar como fallido para evitar reintentos infinitos
      failedAvatars.add(preloadKey);
    } finally {
      // Liberar el flag de carga
      loadingAvatars.delete(preloadKey);
    }
  }, [userId, source?.uri]);

  /**
   * Precarga avatares de usuarios cercanos cuando cambian
   * Implementa debouncing para evitar disparos rápidos consecutivos
   */
  useEffect(() => {
    if (!userId || !imageUrl || !isNearbyUser) return;

    const preloadKey = `${userId}_${imageUrl}`;

    // Verificar si ya fue precargado o falló antes de programar el debounce
    if (preloadedAvatars.has(preloadKey) || failedAvatars.has(preloadKey)) {
      return;
    }

    // Debounce: esperar 300ms antes de precargar para evitar disparos rápidos
    const timeoutId = setTimeout(() => {
      preloadAvatar();
    }, 300);

    // Cleanup: cancelar el timeout si las dependencias cambian antes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [userId, imageUrl, isNearbyUser, preloadAvatar]);

  /**
   * Invalida el caché para un avatar específico
   * Útil cuando se actualiza la imagen del usuario
   * Limpia también los sets globales para permitir reintentos
   */
  const invalidateCache = useCallback(() => {
    if (!cacheKey || !source?.uri) return;
    
    const preloadKey = `${userId}_${source.uri}`;
    
    console.log(`[useCachedAvatar] Invalidando caché: ${cacheKey}`);
    
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error(`[useCachedAvatar] Error invalidando caché:`, error);
    }
    
    // Limpiar sets globales para permitir reintentos
    preloadedAvatars.delete(preloadKey);
    failedAvatars.delete(preloadKey);
    loadingAvatars.delete(preloadKey);
    
    console.log(`[useCachedAvatar] Sets globales limpiados para: ${userId}`);
  }, [cacheKey, userId, source?.uri]);

  /**
   * Guarda metadatos de caché en localStorage
   */
  const saveCacheMetadata = useCallback((metadata) => {
    if (!cacheKey) return;
    
    try {
      const data = {
        timestamp: Date.now(),
        url: source?.uri,
        ...metadata
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`[useCachedAvatar] Metadatos guardados para: ${cacheKey}`);
    } catch (error) {
      console.error(`[useCachedAvatar] Error guardando metadatos:`, error);
    }
  }, [cacheKey, source?.uri]);

  /**
   * Obtiene metadatos de caché desde localStorage
   */
  const getCacheMetadata = useCallback(() => {
    if (!cacheKey) return null;
    
    try {
      const data = localStorage.getItem(cacheKey);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.error(`[useCachedAvatar] Error obteniendo metadatos:`, error);
    }
    return null;
  }, [cacheKey]);

  return {
    source,
    cacheKey,
    isNearbyUser,
    preloadAvatar,
    invalidateCache,
    saveCacheMetadata,
    getCacheMetadata,
  };
}

/**
 * Limpia todos los cachés globales de avatares
 * Útil para debugging o para resetear el estado completamente
 */
export const clearAllAvatarCaches = () => {
  console.log('[useCachedAvatar] Limpiando todos los cachés globales');
  preloadedAvatars.clear();
  failedAvatars.clear();
  loadingAvatars.clear();
};

/**
 * Constante de imagen por defecto para exportar
 */
export { DEFAULT_IMAGE };
